import React from 'react';
import styled from 'styled-components';
import { Grid, Breakpoints, Color, Shadow, Type } from '../app/StyleGuide';
import { OutlineButton } from './Buttons';

const ToolbarTrack = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: ${Grid._5};
  display: flex;
  justify-content: center;
  z-index: 10;
  pointer-events: none;
  & > * {
    pointer-events: auto;
  }
`;

const Toolbar = styled.div<{ $visible: boolean }>`
  display: flex;
  align-items: center;
  gap: ${Grid._3};
  padding: ${Grid._2};
  background: ${Color.white};
  border-radius: ${Grid._6};
  box-shadow: ${Shadow.medium};
  border: 1px solid ${Color.Gray._20};
  width: fit-content;
  opacity: ${props => (props.$visible ? 1 : 0)};
  pointer-events: ${props => (props.$visible ? 'auto' : 'none')};
  transition: opacity 0.35s ease-in-out;
`;

const PageIndicator = styled.span`
  font-size: ${Type.Scale._2};
  color: ${Type.Color.medium};
  min-width: 5ch;
  text-align: center;
`;

const ToolbarSeparator = styled.div`
  width: 1px;
  height: 24px;
  background: ${Color.Gray._20};
  flex-shrink: 0;
`;

const ZoomIndicator = styled.span`
  font-size: ${Type.Scale._2};
  color: ${Type.Color.medium};
  min-width: 3ch;
  text-align: center;
`;

const RoundToolbarButton = styled(OutlineButton)`
  border-radius: 50%;
  width: 40px;
  height: 40px;
  min-width: 40px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  i {
    margin-right: 0;
  }
`;

type PDFViewerToolbarProps = {
  visible: boolean;
  isLoading: boolean;
  pageNumber: number;
  numPages: number | null;
  zoomLevel: number;
  zoomMin: number;
  zoomMax: number;
  onPageUp: () => void;
  onPageDown: () => void;
  onZoomOut: () => void;
  onZoomIn: () => void;
  onDownload: () => void;
};

export default function PDFViewerToolbar({
  visible,
  isLoading,
  pageNumber,
  numPages,
  zoomLevel,
  zoomMin,
  zoomMax,
  onPageUp,
  onPageDown,
  onZoomOut,
  onZoomIn,
  onDownload,
}: PDFViewerToolbarProps) {
  return (
    <ToolbarTrack>
      <Toolbar $visible={visible && !isLoading}>
        <RoundToolbarButton onClick={onPageUp} aria-label="Previous page" disabled={pageNumber <= 1}>
          <i className="fas fa-chevron-up" aria-hidden />
        </RoundToolbarButton>
        <PageIndicator
          role="status"
          aria-live="polite"
          aria-label={numPages != null ? `Page ${pageNumber} of ${numPages}` : 'Page number not available'}
        >
          {numPages != null ? `${pageNumber} / ${numPages}` : '—'}
        </PageIndicator>
        <RoundToolbarButton
          onClick={onPageDown}
          aria-label="Next page"
          disabled={numPages != null && pageNumber >= numPages}
        >
          <i className="fas fa-chevron-down" aria-hidden />
        </RoundToolbarButton>
        <ToolbarSeparator aria-hidden />
        <RoundToolbarButton onClick={onZoomOut} aria-label="Zoom out" disabled={zoomLevel <= zoomMin}>
          <i className="fas fa-search-minus" aria-hidden />
        </RoundToolbarButton>
        <ZoomIndicator role="status" aria-label={`Zoom ${Math.round(zoomLevel * 100)}%`}>
          {Math.round(zoomLevel * 100)}%
        </ZoomIndicator>
        <RoundToolbarButton onClick={onZoomIn} aria-label="Zoom in" disabled={zoomLevel >= zoomMax}>
          <i className="fas fa-search-plus" aria-hidden />
        </RoundToolbarButton>
        <ToolbarSeparator aria-hidden />
        <RoundToolbarButton onClick={onDownload} aria-label="Download PDF">
          <i className="fas fa-download" aria-hidden />
        </RoundToolbarButton>
      </Toolbar>
    </ToolbarTrack>
  );
}
